import ExcelJS from 'exceljs';
import { CommonColumnsType, ExcelColumnsType, SheetNameType, SheetType } from '../types';
import { EXCEL_TYPE } from '@op/constants';
import * as FileSaver from 'file-saver';



// === 共享样式常量（样式复用，避免每行 new 字面量对象） ===
const ROW_FONT = Object.freeze({ name: 'Verdana', size: 12 });
const HEADER_FONT = Object.freeze({ name: 'Verdana', size: 12, bold: true });
const BORDER_DOTTED = Object.freeze({ style: 'dotted' as const });
const ROW_BORDER = Object.freeze({
  top: BORDER_DOTTED,
  left: BORDER_DOTTED,
  bottom: BORDER_DOTTED,
  right: BORDER_DOTTED,
});
const ALIGN_MIDDLE = Object.freeze({ vertical: 'middle' as const });
const ALIGN_CENTER = Object.freeze({
  vertical: 'middle' as const,
  horizontal: 'center' as const,
});




function buildWorksheetColumns(
  columns: CommonColumnsType,
  worksheet: any,
): { worksheetColumns: ExcelColumnsType[]; hasChild: boolean } {
  const worksheetColumns: ExcelColumnsType[] = [];

  const row1: string[] = [];
  const row2: string[] = [];
  let hasChild = false;
  for (const element of columns) {
    const {
      children = [],
      header,
      key,
      width,
      numFmt,
      renderHelper = [],
    } = element;

    const pushItem = {
      ...element,
      header,
      key: key ?? header,
      width: width ?? 20,
      render: key,
      numFmt,
      children,
      renderHelper,
    };

    if (!children || !Array.isArray(children) || children.length === 0) {
      row1.push(header);
      row2.push('');
      worksheetColumns.push(pushItem);
    } else {
      row2.push(
        ...children.map(item => {
          row1.push(header);
          return item.header;
        }),
      );
      for (const childItem of children) {
        const { header } = childItem;
        worksheetColumns.push({
          ...pushItem,
          header,
          key: header || childItem.key || pushItem.key,
          render: childItem.key || pushItem.key,
          numFmt: childItem.numFmt || pushItem.numFmt,
          renderHelper: childItem.renderHelper || pushItem.renderHelper,
          width: childItem.width || pushItem.width,
        });
      }
      hasChild = true;
    }
  }

  worksheet.addRow(row1);
  if (hasChild) {
    worksheet.addRow(row2);
  }

  for (let index = 0; index < worksheetColumns.length;) {
    const { children } = worksheetColumns[index];

    const cell = worksheet.getCell(1, index + 1);
    cell.alignment = ALIGN_MIDDLE;

    if (!children || !Array.isArray(children) || children.length === 0) {
      worksheet.mergeCells(1, index + 1, hasChild ? 2 : 1, index + 1);
      index += 1;
    } else {
      worksheet.mergeCells(1, index + 1, 1, index + children.length);

      cell.alignment = ALIGN_CENTER;

      index += children.length;
    }
  }

  return { worksheetColumns, hasChild };
}

function renderHelperData(
  data: Array<any>,
  worksheetColumns: Array<ExcelColumnsType>,
) {
  return data.map(item => {
    const renderData: string[] = [];
    for (const worksheetColumn of worksheetColumns) {
      const { render, renderHelper = [] } = worksheetColumn;

      if (!render && !renderHelper) continue; // 特殊处理没有render的情况
      if (render instanceof Function) {
        renderData.push(render(item));
      } else {
        let renderItem = '';

        if (render) {
          renderItem = item[render];
        }

        if (
          renderHelper &&
          Array.isArray(renderHelper) &&
          renderHelper.length > 0
        ) {
          for (const fn of renderHelper) {
            renderItem = fn(item, render);
          }
        }
        renderData.push(renderItem);
      }
    }
    return renderData;
  });
}

function addSheetData({
  worksheet,
  worksheetColumns,
  list,
}: {
  worksheet: any;
  worksheetColumns: ExcelColumnsType[];
  list: Array<any>;
}) {
  console.log('渲染数据:', list);

  const data = renderHelperData(list, worksheetColumns);

  worksheet.columns = worksheetColumns;

  worksheet.addRows(data);

  worksheetColumns.forEach((col, index) => {
    if (col?.cellFormat === true) {
      formatPercentageColumn(worksheet, index + 1, data);
    }
  });
}

/**
 * 针对特殊情况格式数据 - 一列里好几种特殊值
 * @param worksheet - exceljs worksheet
 * @param col - 列数
 * @param data - 数据
 * @param rowOffset - 数据开始行数
 */
function formatPercentageColumn(
  worksheet: ExcelJS.Worksheet,
  col: number,
  data: any[],
  rowOffset = 2,
) {
  data.forEach((_row, i) => {
    const cell = worksheet.getCell(i + rowOffset, col);
    const { value } = cell;
    if (typeof value === 'string' && value.includes('%')) {
      const numeric = parseFloat(value.replace('%', ''));
      cell.value = numeric / 100;
      // TODO: 目前只支持百分比，2位数
      cell.numFmt = '0.00%';
    }
  });
}

function renderTableFormat({
  worksheetColumns,
  worksheet,
  total,
  hasChild,
  align = 'auto',
}: {
  worksheet: any;
  align?: 'center' | 'auto';
  hasChild: boolean;
  total: number;
  worksheetColumns: ExcelColumnsType[];
}) {
  for (let index = 0; index < worksheetColumns.length; index += 1) {
    const { numFmt } = worksheetColumns[index];
    if (numFmt) {
      worksheet.getColumn(index + 1).numFmt = numFmt;
    }
  }

  for (let index = 0; index < total; index += 1) {
    const curIndex = (hasChild ? 3 : 2) + index;

    worksheet.getRow(curIndex).font = ROW_FONT;
    if (align === 'center') {
      worksheet.getRow(curIndex).alignment = ALIGN_CENTER;
    }
    worksheet.getRow(curIndex).border = ROW_BORDER;
  }

  const rowIndex = hasChild ? 2 : 1;
  for (let index = 0; index < rowIndex; index += 1) {
    worksheet.getRow(index + 1).font = HEADER_FONT;
    // worksheet.getRow(index + 1).alignment = ALIGN_CENTER;
    worksheet.getRow(index + 1).border = ROW_BORDER;
  }
}

function createOneSheet({
  worksheet,
  columns,
  list,
  align = 'auto',
}: {
  worksheet: any;
  columns: CommonColumnsType;
  list: Array<any>;
  align?: 'center' | 'auto';
}) {
  const { worksheetColumns, hasChild } = buildWorksheetColumns(
    columns,
    worksheet,
  );
  addSheetData({
    worksheet,
    list,
    worksheetColumns,
  });

  renderTableFormat({
    worksheetColumns,
    worksheet,
    total: list.length,
    hasChild,
    align,
  });
}

type CreateExcelType = {
  sheetNames: SheetNameType;
  fileName: string;
  columns: (sheet: SheetType) => CommonColumnsType;
  list: Array<any>;
};



export async function createExcel(
  { list, sheetNames, fileName, columns }: CreateExcelType,
  downloadFile = true,
) {
  const workbook = new ExcelJS.Workbook();
  const isMultiSheet = Array.isArray(sheetNames);
  const sheets: SheetType[] = (isMultiSheet
    ? sheetNames
    : [{ name: sheetNames, key: 'list', hideNoData: false }]) as SheetType[];
  const data: any = isMultiSheet ? list : { list };

  for (const element of sheets) {
    const { name, key, hideNoData = false } = element;
    const downloadData = data[key] || [];
    if (hideNoData && downloadData.length === 0) {
      continue;
    }

    const worksheet = workbook.addWorksheet(name);

    await createOneSheet({ worksheet, list: downloadData, columns: columns(element) });
  }

  try {
    const data = await workbook.xlsx.writeBuffer();
    const blob = new Blob([data], { type: EXCEL_TYPE });

    if (downloadFile) {
      FileSaver.saveAs(blob, fileName);
    } else {
      return blob;
    }
  } catch { }
}

export async function createPageExcel({
  sheetNames,
  fileName,
  columns,
}: Omit<CreateExcelType, 'list'>) {
  const workbook = new ExcelJS.Workbook();
  const isMultiSheet = Array.isArray(sheetNames);
  const sheets: SheetType[] = (isMultiSheet
    ? sheetNames
    : [{ name: sheetNames, key: 'list' }]) as SheetType[];
  const worksheetMap: {
    [key: string]: {
      worksheet: ExcelJS.Worksheet;
      worksheetColumns: ExcelColumnsType[];
      hasChild: boolean;
    };
  } = {};
  for (const element of sheets) {
    const { name } = element;

    const worksheet = workbook.addWorksheet(name);
    const { worksheetColumns, hasChild } = buildWorksheetColumns(
      columns(element),
      worksheet,
    );
    worksheetMap[name] = {
      worksheet,
      worksheetColumns,
      hasChild,
    };
  }

  return {
    add(list: { [key: string]: any[] }) {
      for (const element of sheets) {
        const { name, key } = element;
        const { worksheet, worksheetColumns } = worksheetMap[name];
        const downloadData = list?.[key] || [];
        addSheetData({
          worksheet,
          worksheetColumns,
          list: downloadData,
        });
      }
    },
    finally(total: number) {
      for (const element of sheets) {
        const { name } = element;
        const { worksheet, worksheetColumns, hasChild } = worksheetMap[name];
        renderTableFormat({
          worksheetColumns,
          worksheet,
          total,
          hasChild,
        });
      }
    },
    async create(downloadFile = true) {
      const data = await workbook.xlsx.writeBuffer();
      const blob = new Blob([data], { type: EXCEL_TYPE });

      if (downloadFile) {
        FileSaver.saveAs(blob, fileName);
      } else {
        return blob;
      }
    },
  };
}
